/** Visionary Theme v1.2 — cart drawer */
'use strict';
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('.cart-drawer__panel');
    this.itemsContainer = this.querySelector('[data-cart-items]');
    this.emptyState = this.querySelector('[data-cart-empty]');
    this.footer = this.querySelector('[data-cart-footer]');
    this.subtotalEl = this.querySelector('[data-cart-subtotal]');
    this.checkoutPrice = this.querySelector('[data-checkout-price]');
    this.shippingBar = this.querySelector('[data-free-shipping-bar]');
    this.noteToggle = this.querySelector('[data-note-toggle]');
    this.noteField = this.querySelector('[data-note-field]');
    this.lastTrigger = null;
    this.isOpen = false;
  }
  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick = (event) => {
      const open = event.target.closest('[data-cart-drawer-open], [data-open="cart-drawer"]');
      if (open) { event.preventDefault(); this.lastTrigger = open; this.open(); return; }
      if (event.target.closest('[data-dismiss="cart-drawer"]')) this.close();
    });
    document.addEventListener('keydown', this.onKeyDown = (event) => { if (event.key === 'Escape' && this.isOpen) this.close(); });
    document.addEventListener('cart:updated', this.onCartUpdated = () => this.refresh());
    document.addEventListener('cart:added', this.onCartAdded = () => this.refresh().then(() => this.open()));
    this.addEventListener('click', (event) => {
      const line = event.target.closest('[data-cart-item]');
      if (event.target.closest('[data-quantity-minus]')) this.changeQuantity(line, -1);
      if (event.target.closest('[data-quantity-plus]')) this.changeQuantity(line, 1);
      if (event.target.closest('[data-cart-remove]')) this.removeLine(line);
    });
    this.noteToggle?.addEventListener('click', () => {
      const expanded = this.noteToggle.getAttribute('aria-expanded') === 'true';
      this.noteToggle.setAttribute('aria-expanded', String(!expanded));
      this.noteField.hidden = expanded;
      if (!expanded) this.noteField.querySelector('textarea')?.focus();
    });
    this.refresh();
  }
  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    document.removeEventListener('cart:added', this.onCartAdded);
  }
  async open() {
    this.isOpen = true;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden','false');
    document.documentElement.classList.add('visionary-modal-open');
    await this.refresh();
    setTimeout(() => this.querySelector('[data-dismiss="cart-drawer"]')?.focus(), 30);
  }
  close() {
    this.isOpen = false;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden','true');
    document.documentElement.classList.remove('visionary-modal-open');
    this.lastTrigger?.focus();
  }
  async refresh() {
    if (!window.CartAPI) return;
    try { this.renderCart(await CartAPI.getCart()); } catch (error) { console.error('[Visionary cart drawer]', error); }
  }
  renderCart(cart) {
    document.querySelectorAll('[data-cart-count], .site-header__cart-count').forEach((el) => {
      const text = el.classList.contains('cart-drawer__count') ? (cart.item_count ? `(${cart.item_count})` : '') : String(cart.item_count);
      el.textContent = text;
      el.classList.toggle('visually-hidden', cart.item_count === 0 && !el.classList.contains('cart-drawer__count'));
    });
    const empty = cart.item_count === 0;
    if (this.emptyState) this.emptyState.hidden = !empty;
    if (this.itemsContainer) this.itemsContainer.hidden = empty;
    if (this.footer) this.footer.hidden = empty;
    if (empty) { if (this.itemsContainer) this.itemsContainer.innerHTML=''; this.updateShippingBar(cart); return; }
    if (this.itemsContainer) this.itemsContainer.innerHTML = cart.items.map((item,index)=>this.renderItem(item,index+1)).join('');
    const money = window.Visionary ? Visionary.formatMoney(cart.total_price, window.Shopify?.money_format) : String(cart.total_price);
    if (this.subtotalEl) this.subtotalEl.textContent = money;
    if (this.checkoutPrice) this.checkoutPrice.textContent = `— ${money}`;
    this.updateShippingBar(cart);
  }
  renderItem(item,line) {
    const esc = this.escapeHtml.bind(this);
    const imageUrl = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=180` : '';
    const image = imageUrl ? `<img src="${esc(imageUrl)}" alt="${esc(item.product_title)}" width="80" height="100" loading="lazy">` : '';
    const options = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${esc(item.variant_title)}</p>` : '';
    const properties = Object.entries(item.properties || {}).filter(([,v])=>v).map(([k,v])=>`<p class="cart-item__property">${esc(k.replace(/^_/,''))}: ${esc(String(v))}</p>`).join('');
    const money = (n) => window.Visionary ? Visionary.formatMoney(n, window.Shopify?.money_format) : n;
    return `<div class="cart-item" data-cart-item data-line="${line}">
      <div class="cart-item__media"><a href="${esc(item.url)}">${image}</a></div>
      <div class="cart-item__details"><a href="${esc(item.url)}" class="cart-item__name">${esc(item.product_title)}</a>${options}${properties}
        <div class="cart-item__footer"><div class="cart-item__quantity-wrapper">
          <button type="button" class="quantity-input__btn" data-quantity-minus aria-label="Decrease quantity">−</button>
          <span class="cart-item__qty">${item.quantity}</span>
          <button type="button" class="quantity-input__btn" data-quantity-plus aria-label="Increase quantity">+</button>
        </div><span class="cart-item__line-price">${money(item.final_line_price)}</span>
        <button type="button" class="cart-item__remove" data-cart-remove aria-label="Remove ${esc(item.product_title)}">×</button></div>
      </div></div>`;
  }
  async changeQuantity(itemEl,delta) {
    if (!itemEl || !window.CartAPI) return;
    const line=Number(itemEl.dataset.line); const current=Number(itemEl.querySelector('.cart-item__qty')?.textContent||1);
    itemEl.classList.add('is-loading');
    try { this.renderCart(await CartAPI.changeLine(line,Math.max(0,current+delta))); }
    catch(error){ console.error(error); itemEl.classList.remove('is-loading'); }
  }
  async removeLine(itemEl) { if(!itemEl||!window.CartAPI)return; try{this.renderCart(await CartAPI.changeLine(Number(itemEl.dataset.line),0));}catch(error){console.error(error);} }
  updateShippingBar(cart) {
    if(!this.shippingBar)return; const threshold=Number(this.shippingBar.dataset.threshold||0)*100; if(!threshold)return;
    const percent=Math.min(100,(Number(cart.total_price||0)/threshold)*100); const bar=this.shippingBar.querySelector('[data-progress-bar]'); const msg=this.shippingBar.querySelector('[data-shipping-message]');
    if(bar)bar.style.width=`${percent}%`; if(msg){const formatter=window.Visionary?.formatMoney;const remaining=Math.max(0,threshold-Number(cart.total_price||0));msg.textContent=percent>=100?'You qualify for free shipping!':`Add ${formatter ? formatter(remaining,window.Shopify?.money_format) : remaining} more for free shipping.`;}
  }
  escapeHtml(value){return String(value??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');}
}
if(!customElements.get('cart-drawer'))customElements.define('cart-drawer',CartDrawer);
